import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa'
import Request from '../../helpers/Request'
import logo from '../../assets/logo-white.png'

const EventConfirmation = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('loading') // 'loading', 'success', 'error', 'expired'
  const [message, setMessage] = useState('')
  const [eventData, setEventData] = useState(null)

  useEffect(() => {
    const confirmRegistration = async () => {
      try {
        const response = await Request.get(`/api/events/confirm/${token}`)

        if (response.data.status) {
          setStatus('success')
          setMessage(response.data.message)
          setEventData({
            title: response.data.event_title,
            registration_id: response.data.registration_id
          })
        } else {
          setStatus('error')
          setMessage(response.data.error || 'Confirmation failed')
        }
      } catch (error) {
        console.error('Confirmation error:', error)

        if (error.response?.status === 404) {
          setStatus('expired')
          setMessage('The confirmation link has expired or is invalid. Please try registering again.')
        } else {
          setStatus('error')
          setMessage('An error occurred during confirmation. Please try again or contact support.')
        }
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      confirmRegistration()
    }
  }, [token])

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <FaCheckCircle size={60} className="text-success" />
      case 'error':
        return <FaTimesCircle size={60} className="text-danger" />
      case 'expired':
        return <FaExclamationTriangle size={60} className="text-warning" />
      default:
        return null
    }
  }

  const getStatusTitle = () => {
    switch (status) {
      case 'success':
        return 'Registration Confirmed!'
      case 'error':
        return 'Confirmation Failed'
      case 'expired':
        return 'Link Expired'
      default:
        return 'Processing...'
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'success'
      case 'error':
        return 'danger'
      case 'expired':
        return 'warning'
      default:
        return 'info'
    }
  }

  return (
    <>
      <Helmet>
        <title>Event Registration Confirmation - Siberwander</title>
        <meta name="description" content="Confirm your event registration with Siberwander" />
      </Helmet>

      <div className="py-5" style={{
        background: 'linear-gradient(135deg, #20C6ED 0%, #1037C6 100%)',
        minHeight: '100vh'
      }}>
        <Container>
          <div className="text-center">
            <img src={logo} style={{width:"130px"}} alt=""/>
          </div>
          <Row className="justify-content-center mt-3">
            <Col md={8} lg={6}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-5 text-center">
                  {loading ? (
                    <>
                      <Spinner animation="border" variant="primary" className="mb-3" />
                      <h3>Confirming Your Registration...</h3>
                      <p className="text-muted">Please wait while we process your confirmation</p>
                    </>
                  ) : (
                    <>
                      <div className="mb-4">
                        {getStatusIcon()}
                      </div>

                      <h2 className={`mb-3 text-${getStatusColor()}`}>
                        {getStatusTitle()}
                      </h2>

                      <Alert variant={getStatusColor()} className="text-start">
                        <strong>{message}</strong>
                      </Alert>

                      {eventData && status === 'success' && (
                        <Alert variant="info" className="text-start">
                          <h5>Event Details:</h5>
                          <p className="mb-1"><strong>Event:</strong> {eventData.title}</p>
                          <p className="mb-0"><strong>Status:</strong> Confirmed ✅</p>
                        </Alert>
                      )}

                      <div className="mt-4">
                        {status === 'success' ? (
                          <div>
                            <p className="text-success mb-3">
                              <strong>🎉 Welcome aboard!</strong> Your registration has been successfully confirmed.
                            </p>
                            <p className="text-muted">
                              You will receive a confirmation email shortly with further details about the event.
                            </p>
                          </div>
                        ) : status === 'expired' ? (
                          <p className="text-muted">
                            The confirmation link has expired. Please register again for the event.
                          </p>
                        ) : (
                          <p className="text-muted">
                            If you continue to experience issues, please contact our support team.
                          </p>
                        )}
                      </div>

                      <div className="mt-5">
                        <Link to="/events">
                          <Button variant="primary" className="me-2">
                            View All Events
                          </Button>
                        </Link>

                        <Link to="/">
                          <Button variant="outline-secondary">
                            Back to Home
                          </Button>
                        </Link>
                      </div>

                      {status === 'success' && (
                        <Alert variant="success" className="mt-4">
                          <small>
                            📧 Check your email for the confirmation details and event information.
                            If you don't see it in your inbox, please check your spam folder.
                          </small>
                        </Alert>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>

              <Card className="mt-4 border-0 bg-light">
                <Card.Body className="text-center py-3">
                  <small className="text-muted">
                    Need help? Contact us at{' '}
                    <a href="mailto:info@siberwander.com" className="text-decoration-none">
                      info@siberwander.com
                    </a>
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default EventConfirmation